/**
 * GroupComparisonTable - the generic duplicate-group comparison engine.
 *
 * Renders member-columns × attribute-rows for any object kind described by
 * a `GroupComparisonSchema`:
 *
 *   | {corner}    | member 1 hdr | member 2 hdr | ...   <- schema.memberHeader
 *   | Issues      | chip         | chip         |       <- real errors[] per member
 *   | attr rows…  | value        | value (tint) |       <- diff tinting via getValue
 *   | Ignore      | [x]          | Ignored      |       <- selection checkboxes; gated
 *   legend caption
 *
 * Engine-level concerns (diff detection, Issues chips, note counts, the
 * member ExceptionDrawer, ignore selection, legend) are implemented ONCE
 * here; schemas only describe attributes + identity. The checkboxes only
 * SELECT - the host dialog's footer button confirms, so ignoring a whole
 * group means deliberately ticking every member. A member ignored locally
 * keeps its column visible - the chip flips to Ignored and the checkbox
 * drops - so the comparison context stays stable mid-review.
 */
import React, { useMemo, useState } from 'react'
import {
  Box,
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'

import IssuesChip from '../../../../config/exceptions/shared/IssuesChip'
import type { IssuesChipEntry } from '../../../../config/exceptions/shared/issuesState'
import ExceptionDrawer from '../ExceptionDrawer'
import {
  useExceptionNoteCounts,
  compositeKey,
} from '../../../../hooks/useExceptionNoteCounts'
import { useAuth } from '../../../../contexts/AuthContext'
import {
  canSeeExceptionDrawer,
  canSeeExceptionNotes,
} from '../../../../services/auth'
import { NUMERIC_FONT_SX } from '../../../../theme/numericFont'
import type {
  ComparisonAttr,
  ComparisonMemberBase,
  GroupComparisonSchema,
} from './comparisonTypes'
import { isOpenMemberStatus } from './comparisonTypes'

const DIFF_BG = 'rgba(244, 67, 54, 0.08)'
// Same column-separator treatment as the Compare Sales table, so the two
// comparison surfaces read as one family.
const COLUMN_DIVIDER = '1px solid rgba(0, 0, 0, 0.12)'
// Label column reads as the row key: right-aligned against the divider so
// every label sits flush beside its row's (centered) values.
const LABEL_CELL_SX = {
  fontWeight: 500,
  color: 'text.secondary',
  backgroundColor: 'grey.50',
  verticalAlign: 'top',
  textAlign: 'right',
  borderRight: COLUMN_DIVIDER,
} as const

export interface GroupComparisonTableProps<M extends ComparisonMemberBase> {
  fpCode: string
  members: M[]
  schema: GroupComparisonSchema<M>
  /**
   * Role + action_policy gate from the host dialog. False hides the
   * Ignore row entirely (FP users see comparison + issues only).
   */
  allowIgnoreMembers?: boolean
  /**
   * Members ignored during this dialog session (by tag_id). Their column
   * stays; the Issues chip flips to Ignored and the checkbox drops.
   */
  ignoredTagIds?: ReadonlySet<number>
  /**
   * Members ticked for ignoring (by tag_id). The host dialog's footer
   * button confirms the selection - ticking is the ONLY path to a bulk
   * ignore, so closing a whole group means deliberately ticking every
   * member.
   */
  selectedTagIds?: ReadonlySet<number>
  onToggleMember?: (member: M) => void
  /** Disables the checkboxes while a mutation is in flight. */
  selectionDisabled?: boolean
}

const attrHasDiff = <M,>(attr: ComparisonAttr<M>, members: M[]): boolean => {
  if (attr.neverDiff || members.length < 2) return false
  const first = attr.getValue(members[0])
  return members.some((m) => attr.getValue(m) !== first)
}

export default function GroupComparisonTable<M extends ComparisonMemberBase>({
  fpCode,
  members,
  schema,
  allowIgnoreMembers = false,
  ignoredTagIds,
  selectedTagIds,
  onToggleMember,
  selectionDisabled = false,
}: GroupComparisonTableProps<M>) {
  const [drawerMember, setDrawerMember] = useState<M | null>(null)
  const { user } = useAuth()
  const notesGated = canSeeExceptionNotes(user)
  const drawerGated = canSeeExceptionDrawer(user)

  // Notes ride the member's duplicate-tag tuple - same anchor the tables
  // and drawer use, so counts agree across surfaces. Evo/PSC only.
  const noteCountRows = useMemo(() => {
    if (!notesGated) return []
    return members.map((m) => ({
      exception_code: schema.exceptionCode,
      data_domain: schema.domain,
      object_id: schema.memberId(m),
    }))
  }, [notesGated, members, schema])
  const { counts: noteCounts, invalidate: invalidateNoteCount } =
    useExceptionNoteCounts(fpCode, noteCountRows)

  // A locally-ignored member keeps its column: move its duplicate-tag
  // entry from the open chip list to the closed list (kind: 'ignored')
  // so the chip reads Ignored without a refetch.
  const chipEntriesFor = (
    m: M,
  ): { errors: IssuesChipEntry[]; closed: IssuesChipEntry[] } => {
    const errors = m.errors ?? []
    const closed = m.recently_closed_errors ?? []
    if (m.tag_id == null || !ignoredTagIds?.has(m.tag_id)) {
      return { errors, closed }
    }
    const moved = errors
      .filter((e) => e.tag_id === m.tag_id)
      .map((e) => ({ ...e, kind: 'ignored' as const }))
    return {
      errors: errors.filter((e) => e.tag_id !== m.tag_id),
      closed: [...moved, ...closed],
    }
  }

  const memberIsIgnorable = (m: M): boolean =>
    m.tag_id != null
    && isOpenMemberStatus(m.tag_status)
    && !ignoredTagIds?.has(m.tag_id)

  const showIgnoreRow =
    allowIgnoreMembers && !!onToggleMember
    && members.some(
      (m) => m.tag_id != null && isOpenMemberStatus(m.tag_status),
    )

  const minWidth = schema.minColumnWidth ?? 200
  // Vertical separators between member columns (none after the last - the
  // table border closes the grid).
  const memberDivider = (idx: number) =>
    idx < members.length - 1 ? { borderRight: COLUMN_DIVIDER } : {}
  const drawerTuple = drawerMember
    ? {
        exception_code: schema.exceptionCode,
        data_domain: schema.domain,
        object_id: schema.memberId(drawerMember),
        exception_tag_id: drawerMember.tag_id ?? undefined,
        exception_status: drawerMember.tag_status ?? undefined,
        errors: drawerMember.errors ?? [],
        recently_closed_errors: drawerMember.recently_closed_errors ?? [],
      }
    : null

  return (
    <Box sx={{ p: 2 }}>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'grey.50' }}>
              <TableCell
                sx={{
                  width: 170,
                  fontWeight: 600,
                  textAlign: 'right',
                  borderRight: COLUMN_DIVIDER,
                }}
              >
                {schema.cornerLabel}
              </TableCell>
              {members.map((m, i) => (
                <TableCell
                  key={schema.memberId(m)}
                  sx={{
                    fontWeight: 600,
                    verticalAlign: 'top',
                    textAlign: 'center',
                    minWidth,
                    ...memberDivider(i),
                  }}
                >
                  {/* inline-block wrapper: the block centers in the cell
                    * while the header's own layout stays left-aligned. */}
                  <Box sx={{ display: 'inline-block', textAlign: 'left' }}>
                    {schema.memberHeader(m)}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell sx={{ ...LABEL_CELL_SX, verticalAlign: 'middle' }}>
                Issues
              </TableCell>
              {members.map((m, i) => {
                const { errors, closed } = chipEntriesFor(m)
                const k = compositeKey({
                  exception_code: schema.exceptionCode,
                  data_domain: schema.domain,
                  object_id: schema.memberId(m),
                })
                return (
                  <TableCell
                    key={schema.memberId(m)}
                    sx={{
                      verticalAlign: 'middle',
                      textAlign: 'center',
                      ...memberDivider(i),
                    }}
                  >
                    <IssuesChip
                      errors={errors}
                      recentlyClosed={closed}
                      noteCount={notesGated ? (noteCounts[k] ?? 0) : 0}
                      onOpenDrawer={
                        drawerGated ? () => setDrawerMember(m) : undefined
                      }
                    />
                  </TableCell>
                )
              })}
            </TableRow>
            {schema.attrs.map((attr) => {
              const diff = attrHasDiff(attr, members)
              return (
                <TableRow key={attr.key}>
                  <TableCell sx={LABEL_CELL_SX}>{attr.label}</TableCell>
                  {members.map((m, i) => (
                    <TableCell
                      key={schema.memberId(m)}
                      sx={{
                        backgroundColor: diff ? DIFF_BG : undefined,
                        verticalAlign: 'top',
                        fontSize: '0.875rem',
                        wordBreak: 'break-word',
                        // All data cells center for a uniform grid; the
                        // label column right-aligns against the divider.
                        textAlign: attr.align ?? 'center',
                        // Tabular digits (same token as Compare Sales) so
                        // numbers column-align across members.
                        ...(attr.numeric ? NUMERIC_FONT_SX : {}),
                        ...memberDivider(i),
                      }}
                    >
                      {attr.render
                        ? attr.render(m)
                        : String(attr.getValue(m) ?? '-')}
                    </TableCell>
                  ))}
                </TableRow>
              )
            })}
            {showIgnoreRow && (
              <TableRow>
                <TableCell sx={{ ...LABEL_CELL_SX, verticalAlign: 'middle' }}>
                  Ignore
                </TableCell>
                {members.map((m, i) => (
                  <TableCell
                    key={schema.memberId(m)}
                    sx={{
                      verticalAlign: 'middle',
                      textAlign: 'center',
                      py: 0,
                      ...memberDivider(i),
                    }}
                  >
                    {memberIsIgnorable(m) ? (
                      <Checkbox
                        size="small"
                        checked={selectedTagIds?.has(m.tag_id as number) ?? false}
                        onChange={() => onToggleMember!(m)}
                        disabled={selectionDisabled}
                        inputProps={{
                          'aria-label': `Ignore ${schema.memberTitle(m)}`,
                        }}
                      />
                    ) : m.tag_id != null && ignoredTagIds?.has(m.tag_id) ? (
                      <Typography variant="caption" color="text.disabled">
                        Ignored
                      </Typography>
                    ) : null}
                  </TableCell>
                ))}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Legend says what the tint MEANS, not how it's styled. */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mt: 1 }}
      >
        <Box
          component="span"
          sx={{
            display: 'inline-block',
            width: 12,
            height: 12,
            borderRadius: 0.5,
            backgroundColor: DIFF_BG,
            border: '1px solid rgba(244, 67, 54, 0.25)',
            mr: 0.75,
            verticalAlign: 'text-top',
          }}
        />
        Highlighted cells = value differs between duplicates
      </Typography>
      {drawerGated && (
        <ExceptionDrawer
          open={drawerMember !== null}
          onClose={() => setDrawerMember(null)}
          row={drawerTuple}
          fpCode={fpCode}
          // The compare view is scoped to one exception - its code is the
          // current-exception bias, so float + auto-expand it in the drawer.
          focusCode={schema.exceptionCode}
          exceptionTypeName={schema.exceptionTypeName}
          getRowTitle={() =>
            drawerMember ? schema.memberTitle(drawerMember) : ''
          }
          onCountDelta={(delta) => {
            if (!drawerMember) return
            invalidateNoteCount(
              {
                exception_code: schema.exceptionCode,
                data_domain: schema.domain,
                object_id: schema.memberId(drawerMember),
              },
              delta,
            )
          }}
        />
      )}
    </Box>
  )
}
